import React, { useEffect, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction';
import './AttendanceCalendar.scss'
import useAxios from '../../helpers/Hooks/useAxios';
import { URLS } from '../../helpers/URLS';
import moment from 'moment';
import CircleIcon from '@mui/icons-material/Circle';

import { Grid, Typography, Box } from '@mui/material';
import { holidayList } from '../../helpers/Holidays';
/* Softer versions of the original day colours. The legend dot and the day
   cell read from the same value, so the key always matches the grid. */
/* Dates come out of the holiday list as DD/MM/YYYY. */
const holidayDates = new Set(
    holidayList.map((h) => moment(h.date, 'DD/MM/YYYY').format('YYYY-MM-DD'))
);

const holidayName = (key) => {
    const match = holidayList.find(
        (h) => moment(h.date, 'DD/MM/YYYY').format('YYYY-MM-DD') === key);
    return match ? match.holiday : 'Holiday';
};

/* A day is on leave when it falls anywhere in an approved request, inclusive
   of both ends. The previous check compared with isSame(a, b) where the second
   argument should be a unit like 'day' — passing a date there made it compare
   exact milliseconds, so it never matched and leave days were painted as
   missing reports instead. */
const isOnLeave = (date, leaves = []) =>
    leaves.some((leave) => moment(date).isBetween(
        moment(leave.startDate).startOf('day'),
        moment(leave.endDate).endOf('day'),
        'day', '[]'));

const DAY_COLORS = {
    submitted: '#86d6ae',
    missing:   '#f2a49c',
    leave:     '#f8d194',
    holiday:   '#a3cdf2',
    weekend:   '#edf1f6',
};


const AttendanceCalendar = (props) => {
    const { onSelectDate } = props;
    const { fetchData } = useAxios();

    const [calendarData, setCalendarData] = useState({});
    const [events, setEvents] = useState([]);


    const getCalendarData = async (start, end) => {
        const startDate = moment(start).format('YYYY-MM-DD');
        const endDate = moment(end).format('YYYY-MM-DD');
        const response = await fetchData({
            url: URLS.getDashboardCalendarData(startDate, endDate),
            method: "get",
        });
        if (response.status === 200) {
            setCalendarData(response.data);
            generateEvents(response.data, startDate, endDate)
        }
    }

    const generateEvents = (data, startDate, endDate) => {
        let reportingDates = [];
        for (var m = moment(startDate); m.isSameOrBefore(new Date()); m.add(1, 'd')) {
            const weekName = moment(m).format("d");
            const key = m.format('YYYY-MM-DD');

            /* a report is only owed on a working day that is not a holiday
               and not covered by leave */
            if (![0, 6].includes(Number(weekName))
                && !holidayDates.has(key)
                && !isOnLeave(m, data.leaves)) {
                reportingDates.push(key);
            }
        }

        const workReportEvents = reportingDates.map(rdate => {
           
            const reportExist = data.dailyWorkReports.findIndex(report => moment(report.date).format('YYYY-MM-DD') === rdate);
            if(data.dailyWorkReports[reportExist]?.activityID === 'MLPA10'){
                return {
                    type: 'Holiday',
                    start: rdate,
                    end: rdate,
                    display: 'background',
                    backgroundColor: DAY_COLORS.holiday
                }

            } else {
            return {
                type: 'working',
                start: rdate,
                end: rdate,
                display: 'background',
                backgroundColor: reportExist === -1 ? DAY_COLORS.missing : DAY_COLORS.submitted
            }
        }
        })

        const leaveEvents = data.leaves.map(leave => {
            return {
                type: 'leave',
                start: moment(leave.startDate).format('YYYY-MM-DD'),
                end: moment(leave.endDate).add(1, 'd').format('YYYY-MM-DD'),
                display: 'background',
                backgroundColor: DAY_COLORS.leave,
                extendedProps: { label: leave.leaveTypeName || 'On leave' }
            }
        })

        /* every public holiday inside the range on view */
        const holidayEvents = [];
        for (let h = moment(startDate); h.isSameOrBefore(moment(endDate)); h.add(1, 'd')) {
            const key = h.format('YYYY-MM-DD');
            if (!holidayDates.has(key)) continue;

            holidayEvents.push({
                type: 'holiday',
                start: key,
                end: key,
                display: 'background',
                backgroundColor: DAY_COLORS.holiday,
                /* the name is carried, not rendered — a background event
                   draws its title over the date number */
                extendedProps: { label: holidayName(key) }
            });
        }

        const weekends = []
        for (let w = moment(startDate); w.isSameOrBefore(new Date(endDate)); w.add(1, 'd')) {
            const weekName = moment(w).format("d");
            if ([0, 6].includes(Number(weekName))) {
                weekends.push(w.format('YYYY-MM-DD'));
            }
        }
        const disabledWeekEnds = weekends.map(weekend => {
            return {
                type: 'weekend',
                start: moment(weekend).format('YYYY-MM-DD'),
                end: moment(weekend).format('YYYY-MM-DD'),
                display: 'background',
                backgroundColor: DAY_COLORS.weekend
            }
        })

        /* Order matters: FullCalendar paints background events in sequence, so
           the later ones win where they overlap. Weekends first, then the
           report state, then the two that should always show through. */
        setEvents([...disabledWeekEnds, ...workReportEvents, ...holidayEvents, ...leaveEvents])
    }


    const handleDateClick = (arg) => {
        let isDateOnLeave = calendarData?.leaves.filter(leave => {
            let isBetween = moment(arg.date).isBetween(moment(leave.startDate), moment(leave.endDate).add(1, 'd'));
            let isSame = moment(arg.date).isSame(moment(leave.startDate), moment(leave.endDate).add(1, 'd'));
            if (isBetween || isSame) {
                return true
            }
            return false;
        })
        if (isDateOnLeave.length === 0 && moment(arg.date).isSameOrBefore(new Date())) {
            onSelectDate(moment(arg.date).format('YYYY-MM-DD'))
        }
    }



    return (
        <div className='calendar-block'>
            <FullCalendar plugins={[dayGridPlugin, interactionPlugin]}
                initialView='dayGridMonth'
                dateClick={handleDateClick}
                // selectable
                firstDay={0}
                aspectRatio={3.2}
                headerToolbar={{
                    start: 'prev',
                    center: 'title',
                    end: 'next'
                }}
                events={events}
                /* the day cell says what it is on hover, instead of printing
                   the text into the grid */
                eventDidMount={(info) => {
                    const label = info.event.extendedProps?.label
                        || (info.event.extendedProps?.type === 'leave' ? 'On leave' : '');
                    if (label) info.el.setAttribute('title', label);
                }}
                rerenderDelay={true}
                datesSet={(dateInfo) => {
                    getCalendarData(dateInfo.start, dateInfo.end)
                }}
            />

            <Box p={2}>
                <Grid container>
                    <Grid item md={3}>
                        <Typography >
                            <CircleIcon sx={{ color: DAY_COLORS.submitted }} /> Report submitted
                        </Typography>
                    </Grid>
                    <Grid item md={3}>
                        <Typography >
                            <CircleIcon sx={{ color: DAY_COLORS.missing }} /> Report not submitted
                        </Typography>
                    </Grid>
                    <Grid item md={3}>
                        <Typography >
                            <CircleIcon sx={{ color: DAY_COLORS.leave }} /> Leave
                        </Typography>
                    </Grid>
                    <Grid item md={3}>
                        <Typography >
                            <CircleIcon sx={{ color: DAY_COLORS.holiday }} /> Holiday
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

        </div>
    )
}

export default AttendanceCalendar;
